import { Util } from "./util";

/** Native app features; values are a contract with App_Mobile's `AppCapabilities` -- add new ones to both. */
export enum AppCapability {
    Biometric = "biometric",
}

interface ICapabilitySource {
    queryParam: string;
    storageKey: string;
}

/** What the build's code supports (`?caps=`). */
const BUILD_SOURCE: ICapabilitySource = { queryParam: "caps", storageKey: "appCapabilities" };
/** What the phone can do right now (`?deviceCaps=`), e.g. biometrics set up. */
const DEVICE_SOURCE: ICapabilitySource = { queryParam: "deviceCaps", storageKey: "appDeviceCapabilities" };

// Roughly 70 names; a longer list is ignored entirely.
const MAX_RAW_LENGTH = 1024;
const KNOWN_CAPABILITIES = new Set<string>(Object.values(AppCapability));

/** Keeps only known names. UI hint only -- anyone can set `?caps=`, so never use it to authorize. */
export function parseAppCapabilities(raw: string | null | undefined): AppCapability[] {
    if (!raw || raw.length > MAX_RAW_LENGTH)
        return [];

    const caps = raw.split(",")
        .map(x => x.trim().toLowerCase())
        .filter(x => KNOWN_CAPABILITIES.has(x)) as AppCapability[];

    return [...new Set(caps)];
}

/** Saves both lists for the session so they survive redirects; an absent param keeps its earlier value. */
export function captureAppCapabilities(search: string): void {
    captureSource(search, BUILD_SOURCE);
    captureSource(search, DEVICE_SOURCE);
}

/** True only in the app shell when the running build supports the capability. */
export function hasAppCapability(capability: AppCapability): boolean {
    return hasSourceCapability(capability, BUILD_SOURCE);
}

/** True only in the app shell when the phone reported it can use the capability. */
export function deviceSupportsAppCapability(capability: AppCapability): boolean {
    return hasSourceCapability(capability, DEVICE_SOURCE);
}

function captureSource(search: string, source: ICapabilitySource): void {
    const raw = Util.getQueryParamCaseInsensitive(search, source.queryParam);
    if (raw === null)
        return;

    try {
        sessionStorage.setItem(source.storageKey, parseAppCapabilities(raw).join(","));
    } catch {
        // storage unavailable -- features stay gated off
    }
}

function hasSourceCapability(capability: AppCapability, source: ICapabilitySource): boolean {
    if (!Util.isAppView())
        return false;

    let stored: string | null = null;
    try {
        stored = sessionStorage.getItem(source.storageKey);
    } catch {
        return false;
    }

    return parseAppCapabilities(stored).includes(capability);
}
